import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to track unsaved changes in forms and warn users before navigation
 *
 * @param {Object} currentState - Current form state to track
 * @param {boolean} enabled - Whether to enable the warning (default: true)
 * @returns {Object} Hook API with methods and state
 */
const useUnsavedChangesWarning = (currentState, enabled = true) => {
  const [isDirty, setIsDirty] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const initialStateRef = useRef(null);
  const isSubmittingRef = useRef(false);

  // Initialize initial state
  useEffect(() => {
    if (initialStateRef.current === null && currentState) {
      // Deep clone the initial state
      initialStateRef.current = JSON.stringify(currentState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount, currentState excluded to capture initial state

  // Track state changes
  useEffect(() => {
    if (!enabled || initialStateRef.current === null || isSubmittingRef.current) {
      return;
    }

    // Compare current state with initial state
    const currentStateStr = JSON.stringify(currentState);
    const hasChanges = currentStateStr !== initialStateRef.current;

    setIsDirty(hasChanges);
  }, [currentState, enabled]);

  // Handle browser navigation (beforeunload)
  useEffect(() => {
    if (!enabled || !isDirty) {
      return;
    }

    const handleBeforeUnload = (e) => {
      if (isDirty && !isSubmittingRef.current) {
        e.preventDefault();
        e.returnValue = '您有未保存的更改。确定要离开吗？';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, enabled]);

  // Check if navigation should be blocked
  const checkUnsavedChanges = useCallback((navigationCallback) => {
    if (!enabled || !isDirty || isSubmittingRef.current) {
      // No unsaved changes, proceed with navigation
      if (navigationCallback) {
        navigationCallback();
      }
      return true;
    }

    // Has unsaved changes, show dialog
    setPendingNavigation(() => navigationCallback);
    setShowDialog(true);
    return false;
  }, [isDirty, enabled]);

  // Reset dirty state (call after successful save)
  const resetDirty = useCallback(() => {
    if (currentState) {
      initialStateRef.current = JSON.stringify(currentState);
    }
    setIsDirty(false);
    isSubmittingRef.current = false;
  }, [currentState]);

  // Update initial state (useful for edit mode)
  const updateInitialState = useCallback((newState) => {
    initialStateRef.current = JSON.stringify(newState);
    setIsDirty(false);
  }, []);

  // Mark as submitting (prevents warning during save)
  const setIsSubmitting = useCallback((submitting) => {
    isSubmittingRef.current = submitting;
  }, []);

  // Handle dialog actions
  const handleSaveAndLeave = useCallback(async (saveFunction) => {
    try {
      setIsSubmitting(true);
      await saveFunction();
      resetDirty();
      setShowDialog(false);
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
    } catch (error) {
      console.error('Failed to save before leaving:', error);
      setIsSubmitting(false);
      // Keep dialog open on error
      throw error; // Re-throw for dialog to handle
    }
  }, [pendingNavigation, resetDirty, setIsSubmitting]);

  const handleDiscardChanges = useCallback(() => {
    setIsSubmitting(true); // Prevent beforeunload warning
    resetDirty();
    setShowDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation, resetDirty, setIsSubmitting]);

  const handleCancel = useCallback(() => {
    setShowDialog(false);
    setPendingNavigation(null);
  }, []);

  return {
    isDirty,
    showDialog,
    checkUnsavedChanges,
    resetDirty,
    updateInitialState,
    setIsSubmitting,
    handleSaveAndLeave,
    handleDiscardChanges,
    handleCancel,
  };
};

export default useUnsavedChangesWarning;